class IndartxuQuickSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    this.trigger = document.querySelector(`[data-quick-search-trigger][aria-controls="${this.id}"]`);
    this.panel = this.querySelector('.quick-search__panel');
    this.form = this.querySelector('.quick-search__form');
    this.input = this.querySelector('[data-quick-search-input]');
    this.results = this.querySelector('[data-quick-search-results]');
    this.status = this.querySelector('[data-quick-search-status]');
    this.initialResults = this.results?.innerHTML || '';
    this.cache = new Map();
    this.requestController = null;
    this.searchTimer = null;
    this.previousFocus = null;
    this.lifecycleController = new AbortController();

    const signal = this.lifecycleController.signal;
    this.trigger?.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(this.trigger);
    }, { signal });
    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    this.input?.addEventListener('input', () => this.handleInput(), { signal });
    this.form?.addEventListener('submit', (event) => {
      if (this.input?.value.trim()) return;
      event.preventDefault();
      this.input?.focus();
    }, { signal });
  }

  disconnectedCallback() {
    this.lifecycleController?.abort();
    this.requestController?.abort();
    window.clearTimeout(this.searchTimer);
    delete this.dataset.bound;
  }

  open(trigger = document.activeElement) {
    if (!this.hidden) return;
    this.previousFocus = trigger;
    document.querySelector('cart-drawer')?.close?.();
    document.querySelectorAll('.mobile-menu.is-open').forEach((menu) => {
      menu.querySelector('[data-menu-close]')?.click();
    });
    const header = this.trigger?.closest('[data-header]');
    const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
    this.style.setProperty('--quick-search-top', `${headerBottom}px`);
    this.hidden = false;
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('quick-search-open');
    window.requestAnimationFrame(() => this.input?.focus({ preventScroll: true }));
  }

  close({ restoreFocus = true } = {}) {
    if (this.hidden) return;
    this.hidden = true;
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.requestController?.abort();
    window.clearTimeout(this.searchTimer);
    this.removeAttribute('aria-busy');
    document.body.classList.remove('quick-search-open');
    if (restoreFocus && this.previousFocus instanceof HTMLElement && document.contains(this.previousFocus)) {
      this.previousFocus.focus({ preventScroll: true });
    }
  }

  handleClick(event) {
    if (event.target.closest('[data-quick-search-close]')) {
      this.close();
      return;
    }
    const tab = event.target.closest('[data-quick-search-tab]');
    if (tab) this.activateTab(tab);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }

    const tab = event.target.closest('[data-quick-search-tab]');
    if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const tabs = [...this.querySelectorAll('[data-quick-search-tab]')];
      let nextIndex = tabs.indexOf(tab);
      if (event.key === 'ArrowRight') nextIndex = (nextIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (nextIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      this.activateTab(tabs[nextIndex], true);
      return;
    }

    if (event.key !== 'Tab' || this.hidden) return;
    const focusable = [...this.querySelectorAll(
      'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  handleInput() {
    window.clearTimeout(this.searchTimer);
    const query = this.input?.value.trim() || '';
    const minimumCharacters = Number(this.dataset.minCharacters || 2);
    if (query.length < minimumCharacters) {
      this.requestController?.abort();
      this.restoreInitialResults();
      return;
    }
    this.searchTimer = window.setTimeout(() => this.search(query), 300);
  }

  async search(query) {
    const cacheKey = query.toLocaleLowerCase();
    if (this.cache.has(cacheKey)) {
      this.renderResults(this.cache.get(cacheKey));
      return;
    }

    this.requestController?.abort();
    this.requestController = new AbortController();
    this.setLoading(true);
    try {
      const url = new URL(this.dataset.predictiveUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', 'product,collection,page');
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.requestController.signal
      });
      if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const nextResults = parsed.querySelector('[data-predictive-search-results]');
      if (!nextResults) throw new Error('Predictive search response is missing results');
      this.cache.set(cacheKey, nextResults.innerHTML);
      this.renderResults(nextResults.innerHTML);
    } catch (error) {
      if (error.name !== 'AbortError' && this.status) {
        this.status.textContent = this.dataset.errorMessage || '';
      }
    } finally {
      this.setLoading(false);
    }
  }

  renderResults(markup) {
    if (!this.results) return;
    this.results.innerHTML = markup;
    const summary = this.results.querySelector('[data-quick-search-summary]');
    if (this.status) this.status.textContent = summary?.textContent.trim() || '';
  }

  restoreInitialResults() {
    if (!this.results) return;
    this.results.innerHTML = this.initialResults;
    if (this.status) this.status.textContent = '';
    this.setLoading(false);
  }

  activateTab(tab, focus = false) {
    const target = tab.dataset.quickSearchTab;
    this.querySelectorAll('[data-quick-search-tab]').forEach((candidate) => {
      const active = candidate === tab;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-selected', String(active));
      candidate.setAttribute('tabindex', active ? '0' : '-1');
    });
    this.querySelectorAll('[data-quick-search-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.quickSearchPanel !== target;
    });
    if (focus) tab.focus();
  }

  setLoading(loading) {
    this.classList.toggle('is-loading', loading);
    this.toggleAttribute('aria-busy', loading);
  }
}

if (!customElements.get('indartxu-quick-search')) {
  customElements.define('indartxu-quick-search', IndartxuQuickSearch);
}
